// src/components/AIChatWidget.jsx
import { useState, useRef, useEffect } from "react";
import "./AIChatWidget.css";
import API_BASE from "../services/apiClient";

// Frontend never talks to Gemini directly anymore — the API key lives only
// in the Cloud Function's secret (GEMINI_API_KEY), never in browser code.
const AI_CHAT_URL = `${API_BASE}/ai/chat`;

const SYSTEM_PROMPT = `You are a smart and friendly AI sales assistant for Circuit Printing — a premium Canada-based online printing company.

YOUR ROLE & RESPONSIBILITIES:
- Help customers choose the right printing products for their needs
- Explain product options, sizes, materials, finishes, and quantities
- Guide customers through the ordering process
- Answer questions about pricing, turnaround times, and delivery
- Assist with artwork/file requirements and specifications
- Handle complaints or concerns politely and escalate to human support when needed
- Upsell related products naturally when appropriate

OUR PRODUCTS:
- Business Cards (standard, premium, square, rounded corners, spot UV, foil)
- Flyers & Leaflets (A6, A5, A4, DL — single/double sided, gloss or matte)
- Posters (A3, A2, A1, A0 — indoor and outdoor)
- Roll-up & Pop-up Banners (various sizes)
- Brochures (tri-fold, bi-fold, saddle stitched, perfect bound)
- Stickers & Labels (die-cut, vinyl, waterproof, paper)
- Letterheads & Compliment Slips
- Postcards
- Booklets, Catalogues & Magazines
- Large Format Printing

KEY INFORMATION:
- Same-day dispatch available if ordered before 2pm
- Free artwork check on every order — our designers review files before printing
- Canada-wide tracked delivery
- Premium paper stocks and eco-friendly options available
- Pantone colour matching available
- ISO-certified facility
- 100% satisfaction guarantee
- 25+ years in the printing industry

PRICING: Direct customers to the products page or suggest they contact us for custom quotes on large orders.

ARTWORK REQUIREMENTS: Accept PDF, AI, EPS, TIFF, PSD files. Minimum 300 DPI. Bleed and crop marks required. CMYK colour mode preferred.

TONE: Be warm, professional, and concise. Don't be overly salesy. Focus on genuinely helping the customer find the best solution for their needs. Use British English spelling.

If you cannot answer something specific, say you will connect them with a human team member and suggest they visit the Contact page or call us.`;

function BotIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="11" width="18" height="10" rx="2"/>
      <circle cx="12" cy="5" r="2"/>
      <path d="M12 7v4"/>
      <line x1="8" y1="16" x2="8" y2="16"/>
      <line x1="16" y1="16" x2="16" y2="16"/>
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

function SendIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  );
}

function AIChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "bot", text: "Hi! 👋 I'm Circuit Printing's AI assistant. I can help you choose the right products, explain our printing options, pricing, delivery, and more. What can I help you with today?" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPulse, setShowPulse] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(function() {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  useEffect(function() {
    if (isOpen) {
      setShowPulse(false);
      setTimeout(function() {
        if (inputRef.current) inputRef.current.focus();
      }, 300);
    }
  }, [isOpen]);

  async function sendMessage() {
    var userText = input.trim();
    if (!userText || isLoading) return;

    setInput("");
    setMessages(function(prev) { return [...prev, { role: "user", text: userText }]; });
    setIsLoading(true);

    try {
      var history = messages.map(function(m) {
        return { role: m.role, text: m.text };
      });

      var response = await fetch(AI_CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          history: history,
          message: userText,
          systemPrompt: SYSTEM_PROMPT,
        }),
      });

      var data = await response.json();

      if (response.ok && data.reply) {
        setMessages(function(prev) { return [...prev, { role: "bot", text: data.reply }]; });
      } else {
        throw new Error(data.error || "No response");
      }
    } catch (err) {
      setMessages(function(prev) {
        return [...prev, {
          role: "bot",
          text: "Sorry, I'm having a connection issue right now. Please try again or contact our team directly via the Contact page."
        }];
      });
    }

    setIsLoading(false);
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }

  return (
    <div className="ai-chat-widget">
      {isOpen && (
        <div className="ai-chat-window">
          <div className="ai-chat-header">
            <div className="ai-chat-header-info">
              <div className="ai-chat-avatar">
                <BotIcon />
                <span className="ai-online-dot"></span>
              </div>
              <div>
                <strong>Circuit AI Assistant</strong>
                <span>Online · Powered by Gemini</span>
              </div>
            </div>
            <button className="ai-chat-close" onClick={function() { setIsOpen(false); }}>
              <CloseIcon />
            </button>
          </div>

          <div className="ai-chat-messages">
            {messages.map(function(msg, i) {
              return (
                <div key={i} className={"ai-msg ai-msg-" + msg.role}>
                  {msg.role === "bot" && (
                    <div className="ai-msg-avatar"><BotIcon /></div>
                  )}
                  <div className="ai-msg-bubble">{msg.text}</div>
                </div>
              );
            })}
            {isLoading && (
              <div className="ai-msg ai-msg-bot">
                <div className="ai-msg-avatar"><BotIcon /></div>
                <div className="ai-msg-bubble ai-typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef}></div>
          </div>

          <div className="ai-chat-input-area">
            <textarea
              ref={inputRef}
              className="ai-chat-input"
              placeholder="Ask about products, pricing, delivery..."
              value={input}
              onChange={function(e) { setInput(e.target.value); }}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
            <button
              className="ai-chat-send"
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
            >
              <SendIcon />
            </button>
          </div>
        </div>
      )}

      <button
        className={"ai-chat-fab" + (isOpen ? " ai-chat-fab-open" : "")}
        onClick={function() { setIsOpen(function(o) { return !o; }); }}
        aria-label="Open AI Chat"
      >
        {showPulse && <span className="ai-fab-pulse"></span>}
        {isOpen ? <CloseIcon /> : <BotIcon />}
      </button>
    </div>
  );
}

export default AIChatWidget;